import { useApp } from 'context/app-context'
import { useIsMounted } from 'hooks/use-is-mounted'
import Cookies from 'js-cookie'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import { Key } from 'swr'

const appendQueryParam = (url, append) => {
  if (url.includes('?')) {
    url += `&${append}`
  } else {
    url += `?${append}`
  }
  return url
}

/**
 * Returns a function that can send requests to the
 * API and handles the responses or if a key is passed,
 * returns the API response.
 */
function useApi(
  key?: undefined,
): (
  key: Key,
  method?: string,
  data?: any,
  contentAccept?: string,
) => Promise<any>
function useApi<Data = any>(key?: Key): { data: Data; error: string }
function useApi<Data = any>(
  key: Key | undefined,
):
  | { data: Data | Blob; error: string }
  | ((
      key: Key,
      method?: string,
      data?: any,
      contentAccept?: string,
    ) => Promise<Data | Blob>) {
  const isMounted = useIsMounted()
  const { env } = useApp()
  const { query } = useRouter()

  /**
   * Helper function that can send requests to the
   * API and handles the responses.
   *
   * @param key URL to request or tuple of keys with the URL to request as the first item in the array
   * @param method HTTP method to use (Defaults to GET)
   * @param data Optional request body.
   * @param contentType Optional content type. Will default to json
   */
  const apiRequest = useCallback(
    async <Data = any>(
      key: Key,
      method: RequestInit['method'] = 'GET',
      data: any = null,
      contentAccept: string = 'application/json',
    ): Promise<Data | Blob> => {
      method = method.toUpperCase()
      const url = typeof key === 'string' ? key : key[0]
      const programShareKey = query.programShareKey
      const apiKey = query.apiKey

      let headers: any = {
        Accept: contentAccept,
        'Content-Type': 'application/json',
      }

      let formData: any
      if (method !== 'GET') {
        const token = Cookies.get('csrf')
        formData = JSON.stringify(
          data ? { csrf: token, ...data } : { csrf: token },
        )
      }

      if (typeof window !== 'undefined') {
        if (data instanceof FormData) {
          headers = {}
          formData = data
        }
      }

      return new Promise(async (resolve, reject) => {
        try {
          let fullUrl = `${
            env.NODE_ENV === 'development' ? '' : env.BASE_URL
          }${url}`

          if (programShareKey) {
            fullUrl = appendQueryParam(
              fullUrl,
              `programShareKey=${programShareKey}`,
            )
          }

          if (apiKey) {
            fullUrl = appendQueryParam(fullUrl, `apiKey=${apiKey}`)
          }

          const response = await fetch(fullUrl, {
            method,
            headers: {
              ...headers,
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: formData,
          })
          switch (contentAccept) {
            case 'application/json':
              const json = await response.json()
              if (!response.ok) {
                throw new Error(json.error)
              }
              resolve(json.data)
              break

            case 'application/octet-stream':
              const blob = await response.blob()
              if (!response.ok) {
                reject(blob)
              }
              resolve(blob)
              break

            default:
              break
          }
        } catch (error) {
          if (error.message.charAt(0) === '[') {
            reject(JSON.parse(error.message))
          } else {
            reject(error.message)
          }
        }
      })
    },
    [],
  )

  const [data, setData] = useState<Data | Blob>()
  const [error, setError] = useState<string>(null)

  useEffect(() => {
    const getData = async (key: Key) => {
      try {
        const response = await apiRequest<Data>(key)

        if (isMounted()) {
          setData(response)
          setError(null)
        }
      } catch (error) {
        if (isMounted()) {
          setError(error)
        }
      }
    }
    if (key) {
      getData(key)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  if (typeof key === 'undefined') {
    return apiRequest
  }

  return { data, error }
}

export default useApi
